/**
 * MessageBubble — pure presentational chat bubble. Renders an attribution
 * stripe (3px, agent identity color), a small uppercased mono header (agent
 * code/displayName + timestamp), the message body, optional inline tool-call
 * chips, optional citations, and an optional streaming caret.
 *
 * Post-ADR-001 (Tailwind v4 + Radix UI): no Cloudscape imports. The bubble
 * surface follows the editorial Forest aesthetic — `bg-wayne-surface` for
 * agent turns, `bg-wayne-surface-2` for user turns; hairline border on both;
 * `rounded-xl` for a soft chat card.
 *
 * What this molecule is NOT:
 *   - It does not call markdown or syntax highlighters. The chat organism
 *     plumbs Markdown + Shiki so this molecule stays a pure content vehicle
 *     and Storybook stories can render arbitrary children without lugging
 *     the renderers.
 *   - It does not own WebSocket subscriptions. `streaming: true` only adds a
 *     blinking-caret affordance and an `aria-busy="true"` flag.
 *   - It does not render `dangerouslySetInnerHTML`. Children are React
 *     nodes; sanitization is the caller's job (CLAUDE.md §9).
 */
import { type ReactNode, type ReactElement, useState, useCallback } from 'react';
import { Bookmark, ThumbsUp, ThumbsDown } from 'lucide-react';
import { AGENTS, type AgentId } from '@/tokens/colors';
import { createMemory } from '@/api/services/memory.service';
import ToolInvocationChip from './ToolInvocationChip';
import type { ToolInvocation } from './ToolInvocationChip';

export type MessageRole = 'user' | 'agent' | 'system';

export interface MessageCitation {
  readonly id: string;
  readonly label: string;
  readonly href?: string;
}

export interface MessageBubbleProps {
  readonly role: MessageRole;
  /** Required when `role === 'agent'`. */
  readonly agent?: AgentId;
  readonly content: ReactNode;
  /** Plain-text version of content for saving to memory. */
  readonly rawContent?: string;
  /** ISO-8601 timestamp; rendered as a localized time. */
  readonly timestamp?: string;
  /** When true, the bubble shows a streaming caret + sets `aria-busy`. */
  readonly streaming?: boolean;
  readonly toolCalls?: readonly ToolInvocation[];
  readonly citations?: readonly MessageCitation[];
  /** Renders an inline error indicator beside the header. */
  readonly errored?: boolean;
}

export default function MessageBubble({
  role,
  agent,
  content,
  rawContent,
  timestamp,
  streaming = false,
  toolCalls,
  citations,
  errored = false,
}: MessageBubbleProps): ReactElement {
  const identity = role === 'agent' && agent ? AGENTS[agent] : null;
  const stripeColor = identity ? `var(--agent-${identity.id})` : 'transparent';

  // User bubbles intentionally have no stripe + a slightly different surface
  // tone, so they read as "your turn" against the cream agent surface.
  const isUser = role === 'user';
  const surfaceClass = isUser ? 'bg-wayne-surface-2' : 'bg-wayne-surface';

  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [feedback, setFeedback] = useState<'up' | 'down' | null>(null);

  const handleSaveToMemory = useCallback(async () => {
    if (saveState !== 'idle' || !rawContent) return;
    setSaveState('saving');
    try {
      await createMemory({
        kind: 'reference',
        content: rawContent,
        ...(agent ? { writtenBy: agent } : {}),
      });
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 2000);
    } catch {
      setSaveState('idle');
    }
  }, [saveState, rawContent, agent]);

  const ariaLabel =
    role === 'agent' && identity
      ? `${identity.displayName} message${streaming ? ' (streaming)' : ''}${errored ? ' (error)' : ''}`
      : isUser
        ? 'You'
        : 'System message';

  return (
    <article
      aria-label={ariaLabel}
      aria-busy={streaming ? 'true' : 'false'}
      data-role={role}
      data-agent={agent ?? ''}
      className={`group flex gap-3 ${surfaceClass} border border-wayne-hairline rounded-xl p-4 text-wayne-ink`}
      style={{ fontSize: 14, lineHeight: 1.55 }}
    >
      {!isUser ? (
        <div
          aria-hidden="true"
          className="rounded-sm self-stretch shrink-0"
          style={{ width: 3, background: stripeColor }}
        />
      ) : null}
      <div className="flex-1 min-w-0">
        {!isUser ? (
          <div
            className="flex items-center gap-2 mb-1 font-mono uppercase text-wayne-ink-3"
            style={{ fontSize: 11, letterSpacing: '0.04em' }}
          >
            <span
              className="font-medium"
              style={{ color: identity ? stripeColor : 'var(--wayne-ink-3)' }}
            >
              {identity ? identity.code : 'SYS'}
            </span>
            {identity ? (
              <span className="text-wayne-ink-3 font-medium">
                {identity.displayName}
              </span>
            ) : null}
            {timestamp ? <span>· {formatTime(timestamp)}</span> : null}
            {errored ? (
              <span className="text-wayne-red font-semibold" aria-label="Errored">
                · error
              </span>
            ) : null}
          </div>
        ) : null}
        <div className="break-words">
          {content}
          {streaming ? <StreamingCaret /> : null}
        </div>
        {toolCalls && toolCalls.length > 0 ? (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {toolCalls.map((tc) => (
              <ToolInvocationChip key={tc.id} {...tc} />
            ))}
          </div>
        ) : null}
        {citations && citations.length > 0 ? (
          <div
            className="mt-2 flex flex-wrap gap-1.5"
            style={{ fontSize: 12 }}
          >
            {citations.map((c, i) => (
              <a
                key={c.id}
                href={c.href ?? '#'}
                target={c.href ? '_blank' : undefined}
                rel={c.href ? 'noopener noreferrer' : undefined}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-wayne-hairline text-wayne-accent no-underline hover:bg-wayne-surface-2"
              >
                [{i + 1}] {c.label}
              </a>
            ))}
          </div>
        ) : null}
        {role === 'agent' && rawContent ? (
          <div className="mt-2 flex items-center gap-1.5" style={{ transition: 'opacity 150ms ease' }}>
            <button
              type="button"
              aria-label={saveState === 'saved' ? 'Saved to memory' : 'Save to memory'}
              onClick={handleSaveToMemory}
              disabled={saveState === 'saving'}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-wayne-ink-3 hover:text-wayne-accent hover:bg-wayne-surface-2 transition-opacity ${saveState === 'idle' ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'}`}
              style={{ fontSize: 11 }}
            >
              <Bookmark size={12} aria-hidden="true" />
              <span>{saveState === 'saved' ? 'Saved to memory' : 'Save to memory'}</span>
            </button>
            <button
              type="button"
              aria-label="Mark as helpful"
              onClick={() => setFeedback((prev) => (prev === 'up' ? null : 'up'))}
              className={`inline-flex items-center justify-center p-1 rounded hover:bg-wayne-surface-2 transition-opacity transition-colors ${feedback === 'up' ? 'opacity-100 text-wayne-accent' : 'opacity-0 group-hover:opacity-100 text-wayne-ink-3 hover:text-wayne-accent'}`}
              style={{ transition: 'opacity 150ms ease, color 150ms ease' }}
            >
              <ThumbsUp size={12} aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Mark as not helpful"
              onClick={() => setFeedback((prev) => (prev === 'down' ? null : 'down'))}
              className={`inline-flex items-center justify-center p-1 rounded hover:bg-wayne-surface-2 transition-opacity transition-colors ${feedback === 'down' ? 'opacity-100 text-wayne-accent' : 'opacity-0 group-hover:opacity-100 text-wayne-ink-3 hover:text-wayne-accent'}`}
              style={{ transition: 'opacity 150ms ease, color 150ms ease' }}
            >
              <ThumbsDown size={12} aria-hidden="true" />
            </button>
          </div>
        ) : null}
      </div>
    </article>
  );
}

/**
 * Streaming caret — a 1ch-wide block at the end of the streaming text.
 * Uses Tailwind's built-in `animate-pulse` so we honor `prefers-reduced-motion`
 * via the global stylesheet.
 */
function StreamingCaret(): ReactElement {
  return (
    <span
      aria-hidden="true"
      className="inline-block bg-wayne-ink animate-pulse align-baseline"
      style={{ width: '1ch', height: '1em', marginLeft: 2 }}
    />
  );
}

function formatTime(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return '';
  return new Date(t).toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
}
